/* eslint-disable no-console */
import { isPlatformBrowser } from "@angular/common";
import { Component, DestroyRef, ElementRef, Inject, OnInit, PLATFORM_ID, ViewChild } from "@angular/core";
import { DomSanitizer, SafeResourceUrl, Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { EAppEventName } from "@core-constants/app-events.const";
import { BroadcastService } from "@shared-services/broadcast.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslateService } from "@shared-services/translate.service";
import { RouteHelper } from "@core-helpers/route.helper";
import { RedeemManager } from "@core-managers/redeem.manager";
import { EInitiatives, EStatusInitiatives, Initiatives } from "@core-constants/initiatives.const";
import { ToastService } from "@shared-services/toast.service";
import { ReferenceConst } from "@core-constants/reference.const";
import { IInitiative } from "@shared-base/generic-clases.base";
import { AssetsService } from "@shared-services/assets.service";

@Component({
  selector: 'app-initiatives',
  templateUrl: './initiatives.component.html',
  styleUrls: ['./initiatives.component.css']
})
export class InitiativesComponent implements OnInit
{
  @ViewChild('containerStep3') private containerStep3?: ElementRef;

  public couponId: string | null = null;
  public initiativeId: string | null;
  public domain: string | null = null;
  public showDomainInfo: boolean = true;
  public showButtonStarted: boolean = false;
  public textButtonStarted: string = "Empezar ahora";
  public selectedInitiative: IInitiative | undefined;

  public whatsAppUrl: string = ReferenceConst.WhatsAppUrl;
  public helpCenterTopicsUrl: string = ReferenceConst.HelpCenterAkkyTopicsUrl;
  public helpCenterAkkyUserCreateUrl: SafeResourceUrl;
  public helpCenterAkkyRegistrantContactUrl: SafeResourceUrl;
  public helpCenterAkkyInitialConfigSiteUrl: SafeResourceUrl;
  constructor(@Inject(PLATFORM_ID) private platformId,
    private titleService: Title,
    public translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private routeHelper: RouteHelper,
    protected redeemManager: RedeemManager,
    private assetsService: AssetsService,
    private router: Router,
    protected toast: ToastService,
    public sanitizer: DomSanitizer,
    private destroyRef$: DestroyRef
  ) { }

  public ngOnInit(): void
  {
    this.activatedRoute.queryParamMap.subscribe(params =>
    {
      this.redeemManager.initiativeId = this.initiativeId = params.get('id') || 'default';
      this.redeemManager.domain = this.domain = params.get('domain');
      this.redeemManager.status = EStatusInitiatives.Initial;

      if (this.hasDomain && !this.isValidDomainMx(this.domain))
      {
        this.domain = null;
        this.redirectWithoutDomain();
      }

      if (this.initiativeId)
      {
        this.showButtonStarted = true;
      }
      else
      {
        this.showDomainInfo = false;
        this.showButtonStarted = false;
      }

      if (this.hasDomain)
      {
        this.scrollToResult();
      }

      this.loadSelectedInitiative();
    });

    if (!isPlatformBrowser(this.platformId))
    {
      return;
    }

    this.setSupportUrls();
    this.registerEventListeners();
    this.registerLanguageEventListeners();
    this.redeemManager.checkLogin();
  }

  private loadSelectedInitiative(): void
  {
    const baseInitiative = Initiatives.find(item => item.id === (this.redeemManager.initiativeId as unknown as EInitiatives));
    const pathBlob = this.assetsService.getAssetFromBlob("");
    const lang = this.translateService.languageCode.toLocaleLowerCase();

    if (!baseInitiative)
    {
      return;
    }

    this.selectedInitiative = {
      ...baseInitiative,
      title: this.translateService.getElement(
        baseInitiative.title,
        pathBlob,
        lang,
        pathBlob,
        lang
      )
    };

    this.titleService.setTitle(`${this.translateService.getElement(baseInitiative.label)} | Akky`);
  }

  public setSupportUrls(): void
  {
    this.helpCenterAkkyUserCreateUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.helpCenterTopicsUrl + ReferenceConst.HelpCenterAkkyUserCreateUrl);
    this.helpCenterAkkyRegistrantContactUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.helpCenterTopicsUrl + ReferenceConst.HelpCenterAkkyRegistrantContactUrl);
    this.helpCenterAkkyInitialConfigSiteUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.helpCenterTopicsUrl + ReferenceConst.HelpCenterAkkyInitialConfigSiteUrl);
  }

  private registerLanguageEventListeners(): void
  {
    this.translateService.onTranslateChange()
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: () => this.loadSelectedInitiative()
      });
  }

  public registerEventListeners(): void
  {
    BroadcastService.Instance.on(EAppEventName.OnLoginSuccess)
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: () =>
        {
          this.redeemManager.checkLogin();
        }
      });

    BroadcastService.Instance.on(EAppEventName.OnUserLogout)
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: () =>
        {
          this.redeemManager.checkLogin();
        }
      });
  }

  public get noticeOfPrivacyURL(): string
  {
    return `${this.routeHelper.noticeOfPrivacyURL}?lang=${this.translateService.languageCode}`;
  }

  public goToDataPage(): void
  {
    if (!this.redeemManager.isUserLoggedIn)
    {
      BroadcastService.Instance.broadcast(EAppEventName.OnLoginFromLandingInitiatives);
      BroadcastService.Instance.broadcast(EAppEventName.OnOpenLogin);
      this.textButtonStarted = "Continuar";
      return;
    }

    if (!this.initiativeId)
    {
      this.toast.setErrorToast(this.translateService.getElement("Necesitas una iniciativa para continuar."));
      return;
    }

    if (!this.redeemManager.domain)
    {
      this.toast.setErrorToast(this.translateService.getElement("Necesitas un dominio para continuar."));
      return;
    }

    this.redeemManager.status = EStatusInitiatives.InProcess;
    this.router.navigate(['/iniciativas-datos']);
  }

  public get termsConditionsPDF(): string
  {
    return this.selectedInitiative?.termsConditions || '';
  }

  public get hasDomain(): boolean
  {
    return !!this.domain && this.domain.trim().length > 0;
  }

  private isValidDomainMx(domainToSearch): boolean
  {
    const parts = domainToSearch.split('.');
    const domainName = parts[0];

    if (domainName.length < 3)
    {
      return false;
    }

    //si si trae tld, que solo sea .mx y si no es .mx mandar mensaje de error
    if (parts.length > 1)
    {
      if (parts.length > 2 || parts[1].toLowerCase() !== "mx")
      {
        return false;
      }
    }

    return true;
  }

  private redirectWithoutDomain(): void
  {
    this.redeemManager.removeDomain();

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        domain: null
      },
      queryParamsHandling: 'merge'
    });
  }

  private scrollToResult(): void
  {
    // Usamos un pequeño timeout para asegurar que el div ya se renderizó en el DOM
    setTimeout(() =>
    {
      this.containerStep3?.nativeElement.scrollIntoView({
        behavior: 'smooth', // Animación fluida
        block: 'start'      // Alinea al inicio de la pantalla
      });
    }, 100);
  }
}
